// ===========================================================================
//  Group · single-group page (newsfeed + member stories/reels/live carousels)
//  Route: /group/:gid
// ===========================================================================
import { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  Users, Globe, Lock, Settings, Image as ImageIcon, Video, Link2,
  Send, Trash2, Pin, PinOff, Lock as LockIcon, Unlock, CheckCircle2, XCircle,
  AlertTriangle, Shield, UserPlus, UserMinus, MoreVertical, Film, Radio, Clock,
  Upload, X, Heart, Share2, Ban,
} from "lucide-react";
import { toast, Toaster } from "sonner";

const KINDS = [
  { id: "text",  label: "Post",  icon: Send },
  { id: "image", label: "Image", icon: ImageIcon },
  { id: "video", label: "Video", icon: Video },
  { id: "reel",  label: "Reel",  icon: Film },
  { id: "link",  label: "Link",  icon: Link2 },
];

export default function Group() {
  const { gid } = useParams();
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [posts, setPosts] = useState([]);
  const [pending, setPending] = useState([]);
  const [members, setMembers] = useState([]);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [stories, setStories] = useState([]);
  const [reels, setReels] = useState([]);
  const [live, setLive] = useState([]);
  const [tab, setTab] = useState("feed");       // feed | members | settings | moderation

  const canMod = group?.my_role === "owner" || group?.my_role === "admin";
  const canPost = group?.my_status === "active";
  const isMember = canPost;

  const load = async () => {
    try {
      const { data: g } = await api.get(`/groups/${gid}`);
      setGroup(g);
      if (!g.is_stub) {
        const [p, m, s, r, l] = await Promise.all([
          api.get(`/groups/${gid}/posts`),
          api.get(`/groups/${gid}/members`),
          api.get(`/groups/${gid}/stories`),
          api.get(`/groups/${gid}/reels`),
          api.get(`/groups/${gid}/live`),
        ]);
        setPosts(p.data.items || []);
        setMembers(m.data.items || []);
        setStories(s.data.items || []);
        setReels(r.data.items || []);
        setLive(l.data.items || []);
      }
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [gid]);

  // Mod-only data loads lazily when the moderation tab opens.
  useEffect(() => {
    if (tab !== "moderation" || !canMod) return;
    (async () => {
      try {
        const [pp, mm] = await Promise.all([
          api.get(`/groups/${gid}/posts/pending`),
          api.get(`/groups/${gid}/pending`),
        ]);
        setPending(pp.data.items || []);
        setPendingMembers(mm.data.items || []);
      } catch (e) { /* ignore */ }
    })();
  }, [tab, gid, canMod]);

  const join = async () => {
    try {
      const { data } = await api.post(`/groups/${gid}/join`);
      toast.success(data.status === "pending" ? "Join request sent" : "Welcome to the group");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const leave = async () => {
    if (!window.confirm("Leave this group?")) return;
    try {
      await api.post(`/groups/${gid}/leave`);
      toast.success("Left group");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!group) return <div className="max-w-5xl mx-auto p-10 text-white/50">Loading…</div>;

  if (group.is_stub) {
    return (
      <div className="max-w-3xl mx-auto p-10 text-center space-y-4" data-testid="group-stub">
        <Lock className="w-10 h-10 text-nexus-amber mx-auto" />
        <h1 className="font-display font-black text-3xl">{group.name}</h1>
        <div className="text-sm text-white/60">{group.group_type} · {group.member_count || 0} members · Private</div>
        {group.description && <p className="text-white/70 max-w-md mx-auto">{group.description}</p>}
        {user && <button onClick={join} className="btn-nexus mx-auto"><UserPlus className="w-4 h-4" /> Request to join</button>}
      </div>
    );
  }

  return (
    <div className="max-w-[1500px] mx-auto space-y-6" data-testid={`group-page-${gid}`}>
      <Toaster theme="dark" position="top-center" />

      {/* Header */}
      <div className="relative">
        <div className="aspect-[5/1] bg-gradient-to-br from-nexus-violet/40 to-nexus-cyan/30 overflow-hidden rounded">
          {group.cover_url && (
            <img
              src={group.cover_url}
              alt=""
              className="w-full h-full object-cover"
              style={{ objectPosition: `center ${group.cover_pos_y ?? 50}%` }}
            />
          )}
        </div>
        {/* Group avatar — overlapping the banner */}
        <div className="absolute left-6 -bottom-10 w-24 h-24 rounded-full ring-4 ring-nexus-void bg-gradient-to-br from-nexus-violet to-nexus-cyan overflow-hidden flex items-center justify-center">
          {group.avatar_url ? (
            <img src={group.avatar_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <Users className="w-8 h-8 text-white/80" />
          )}
        </div>
        <div className="flex items-end justify-between gap-4 flex-wrap mt-4 px-2 pl-32">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {group.privacy === "private" ? <Lock className="w-4 h-4 text-nexus-amber" /> : <Globe className="w-4 h-4 text-nexus-mint" />}
              <div className="label-eyebrow">{group.group_type} · {group.privacy}</div>
            </div>
            <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mt-1" data-testid="group-name">{group.name}</h1>
            <div className="text-sm text-white/60 mt-1">{group.member_count || 0} members · age {group.age_min}+ · {group.category}</div>
            {group.description && <p className="text-sm text-white/70 mt-2 max-w-2xl">{group.description}</p>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!isMember && user && (
              <button onClick={join} className="btn-nexus !py-2 !px-4 text-[10px]" data-testid="group-join-btn">
                <UserPlus className="w-3 h-3" /> {group.privacy === "private" ? "Request to join" : "Join"}
              </button>
            )}
            {isMember && !canMod && (
              <button onClick={leave} className="btn-ghost !py-2 !px-4 text-[10px]" data-testid="group-leave-btn">
                <UserMinus className="w-3 h-3" /> Leave
              </button>
            )}
            {canMod && (
              <button onClick={() => setTab("settings")} className="btn-ghost !py-2 !px-4 text-[10px]" data-testid="group-settings-btn">
                <Settings className="w-3 h-3" /> Settings
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-white/10 overflow-x-auto">
        {[
          { id: "feed", label: "Newsfeed" },
          { id: "members", label: `Members · ${members.length}` },
          { id: "rules", label: "Rules" },
          ...(canMod ? [{ id: "moderation", label: `Moderation${pending.length || pendingMembers.length ? ` · ${pending.length + pendingMembers.length}` : ""}` }] : []),
          ...(canMod ? [{ id: "settings", label: "Settings" }] : []),
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-5 py-3 text-xs font-display tracking-[0.3em] uppercase transition border-b-2 whitespace-nowrap ${tab === t.id ? "border-nexus-cyan text-nexus-cyan" : "border-transparent text-white/50 hover:text-white"}`}
            data-testid={`group-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Main */}
      {tab === "feed" && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <div className="space-y-4">
            {canPost && <GroupComposer group={group} onPosted={load} />}
            {posts.length === 0 && <div className="text-center py-12 text-white/40 text-sm">No posts yet. Be the first to share.</div>}
            {posts.map((p) => <GroupPostCard key={p.id} post={p} group={group} canMod={canMod} meId={user?.id} onChange={load} />)}
          </div>

          <aside className="space-y-5">
            <CarouselSection title="Member stories" accent="violet" testId="group-stories" count={stories.length}>
              {stories.map((s) => (
                <div key={s.id} className="w-24 flex-shrink-0 relative aspect-[9/16] rounded-md overflow-hidden border border-nexus-violet/40">
                  <img src={s.media_url || s.thumbnail} alt="" className="w-full h-full object-cover" />
                  <div className="absolute bottom-1 left-1 right-1 text-[9px] font-display uppercase tracking-widest text-white/90 truncate">{s.user_name}</div>
                </div>
              ))}
            </CarouselSection>
            <CarouselSection title="Member reels" accent="cyan" testId="group-reels" count={reels.length}>
              {reels.map((r) => (
                <div key={r.id} className="w-24 flex-shrink-0 relative aspect-[9/16] rounded-md overflow-hidden border border-nexus-cyan/40">
                  <video src={r.media_url} muted className="w-full h-full object-cover" />
                  <div className="absolute bottom-1 left-1 right-1 text-[9px] font-display uppercase tracking-widest text-white/90 truncate">{r.user_name}</div>
                </div>
              ))}
            </CarouselSection>
            <CarouselSection title="Live now" accent="crimson" testId="group-live" count={live.length}>
              {live.map((s) => (
                <Link key={s.id} to={`/watch/${s.id}`} className="w-36 flex-shrink-0 group">
                  <div className="aspect-video relative overflow-hidden rounded border border-nexus-crimson/40">
                    {s.thumbnail && <img src={s.thumbnail} alt="" className="w-full h-full object-cover" />}
                    <div className="absolute top-1 left-1 bg-nexus-crimson text-white text-[8px] font-mono px-1.5 py-0.5 rounded">LIVE</div>
                  </div>
                  <div className="text-[10px] text-white/70 truncate mt-1">{s.title}</div>
                </Link>
              ))}
            </CarouselSection>
          </aside>
        </div>
      )}

      {tab === "members" && (
        <MembersList
          members={members}
          group={group}
          canMod={canMod}
          meId={user?.id}
          onChange={load}
        />
      )}

      {tab === "rules" && (
        <div className="space-y-3 max-w-2xl" data-testid="group-rules">
          <div className="label-eyebrow">// group rules</div>
          {(group.rules || []).length === 0 ? (
            <div className="text-sm text-white/50">No rules set yet.</div>
          ) : (
            <ol className="space-y-2 list-decimal list-inside">
              {group.rules.map((r, i) => <li key={i} className="text-sm text-white/80 bg-nexus-surface border border-white/10 rounded p-3">{r}</li>)}
            </ol>
          )}
        </div>
      )}

      {tab === "moderation" && canMod && (
        <ModerationPanel
          groupId={gid}
          pendingPosts={pending}
          pendingMembers={pendingMembers}
          onChange={load}
        />
      )}

      {tab === "settings" && canMod && (
        <GroupSettingsPanel group={group} onSaved={load} />
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
function CarouselSection({ title, accent, testId, count, children }) {
  const accentClass = { violet: "text-nexus-violet", cyan: "text-nexus-cyan", crimson: "text-nexus-crimson" }[accent] || "text-white";
  return (
    <div data-testid={testId}>
      <div className={`label-eyebrow ${accentClass} flex items-center justify-between mb-2`}>
        <span>// {title}</span>
        <span className="text-white/40">{count || 0}</span>
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar bg-nexus-surface rounded p-2 min-h-[80px] items-center">
        {count === 0 && <div className="text-[11px] text-white/40 px-2">Nothing here yet.</div>}
        {children}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// GroupComposer — full-fat composer: text / image (multi-select) / video /
// reel (with description guidance) / link (with auto-preview via
// /api/link-preview). Owner-controlled allowed_media hides any kind the group
// doesn't permit. Uploads use /api/upload/image, which is the same path the
// avatar/banner editor uses — see GroupImageEditor.
// --------------------------------------------------------------------------
function GroupComposer({ group, onPosted }) {
  const allowed = (group.allowed_media || []).filter((k) => KINDS.find((x) => x.id === k));
  const [kind, setKind] = useState(allowed[0] || "text");
  const [content, setContent] = useState("");
  const [mediaUrls, setMediaUrls] = useState([]);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkPreview, setLinkPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const fileRef = useRef(null);

  const resetAll = () => { setContent(""); setMediaUrls([]); setLinkUrl(""); setLinkPreview(null); };

  const pickFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const next = [...mediaUrls];
    for (const f of Array.from(files).slice(0, 10)) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data.url) next.push(data.url);
      } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    }
    setMediaUrls(next);
    setUploading(false);
  };

  const fetchLinkPreview = async () => {
    const u = linkUrl.trim();
    if (!u) return;
    setPreviewing(true);
    try {
      const { data } = await api.get("/link-preview", { params: { url: u } });
      setLinkPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    setPreviewing(false);
  };

  const submit = async () => {
    if (!content.trim() && !mediaUrls.length && !linkUrl.trim()) {
      toast.error("Add something to share"); return;
    }
    setBusy(true);
    try {
      const body = { kind, content };
      if (mediaUrls.length) { body.media_urls = mediaUrls; body.media_url = mediaUrls[0]; }
      if (linkUrl.trim()) { body.link_url = linkUrl.trim(); body.link_preview = linkPreview || null; }
      const { data } = await api.post(`/groups/${group.id}/posts`, body);
      if (data.approval_status === "pending") toast.info("Post sent for approval");
      else toast.success("Posted");
      resetAll();
      onPosted?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Post failed"); }
    setBusy(false);
  };

  const isMedia = kind === "image" || kind === "video" || kind === "reel";

  return (
    <div className="bg-nexus-surface border border-white/10 rounded p-4 space-y-3" data-testid="group-composer">
      {/* Capability banner — tells members exactly what they can share */}
      <div className="text-[10px] text-white/50 flex items-center gap-2 flex-wrap">
        <span className="label-eyebrow !m-0">// all members can post:</span>
        {allowed.map((k) => {
          const K = KINDS.find((x) => x.id === k);
          if (!K) return null;
          return (
            <span key={k} className="inline-flex items-center gap-1 bg-white/5 text-white/70 px-2 py-0.5 rounded">
              <K.icon className="w-3 h-3" /> {K.label}
            </span>
          );
        })}
      </div>
      {/* Kind picker */}
      <div className="flex items-center gap-2 flex-wrap">
        {allowed.map((k) => {
          const K = KINDS.find((x) => x.id === k);
          if (!K) return null;
          const on = kind === k;
          return (
            <button key={k} onClick={() => { setKind(k); setMediaUrls([]); setLinkUrl(""); setLinkPreview(null); }} className={`inline-flex items-center gap-1 px-3 py-1.5 text-[11px] uppercase tracking-widest border rounded ${on ? "bg-nexus-cyan text-black border-nexus-cyan" : "border-white/10 text-white/60"}`} data-testid={`group-compose-${k}`}>
              <K.icon className="w-3 h-3" /> {K.label}
            </button>
          );
        })}
      </div>
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={3}
        placeholder={kind === "reel" ? `Describe your reel (hashtags welcome)…` : kind === "image" ? `Add a caption…` : `Share something with ${group.name}…`}
        className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm"
        data-testid="group-composer-text"
      />

      {kind === "image" && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => pickFiles(e.target.files)}
            data-testid="group-composer-image-input"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-ghost text-[10px]" data-testid="group-composer-image-pick">
              <Upload className="w-3 h-3" /> {uploading ? "Uploading…" : "Add images"}
            </button>
            <div className="text-[10px] text-white/40">Up to 10 images per post · tap any to remove</div>
          </div>
          {mediaUrls.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {mediaUrls.map((u, i) => (
                <button key={i} onClick={() => setMediaUrls(mediaUrls.filter((_, j) => j !== i))} className="relative aspect-square overflow-hidden group" data-testid={`group-composer-image-${i}`}>
                  <img src={u} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition flex items-center justify-center">
                    <X className="w-4 h-4 text-white opacity-0 group-hover:opacity-100" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {(kind === "video" || kind === "reel") && (
        <input
          value={mediaUrls[0] || ""}
          onChange={(e) => setMediaUrls(e.target.value ? [e.target.value] : [])}
          placeholder={kind === "reel" ? "Reel URL (vertical video)" : "Video URL (mp4 / webm)"}
          className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm"
          data-testid="group-composer-video-url"
        />
      )}

      {kind === "link" && (
        <>
          <div className="flex items-center gap-2">
            <input
              value={linkUrl}
              onChange={(e) => { setLinkUrl(e.target.value); setLinkPreview(null); }}
              placeholder="https://…"
              className="flex-1 bg-transparent border border-white/10 px-3 py-2 text-sm"
              data-testid="group-composer-link-url"
            />
            <button onClick={fetchLinkPreview} disabled={!linkUrl.trim() || previewing} className="btn-ghost text-[10px]" data-testid="group-composer-link-preview">
              {previewing ? "Loading…" : "Preview"}
            </button>
          </div>
          {linkPreview && <LinkPreviewCard preview={linkPreview} />}
        </>
      )}

      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] text-white/40">
          {group.require_post_approval ? "Posts go to admin approval queue first" : "Posts are live immediately · members can like, share, and comment"}
        </div>
        <button onClick={submit} disabled={busy} className="btn-nexus !py-1.5 !px-4 text-[10px]" data-testid="group-composer-submit">
          <Send className="w-3 h-3" /> {busy ? "Posting…" : "Post"}
        </button>
      </div>
    </div>
  );
}

function LinkPreviewCard({ preview }) {
  if (!preview) return null;
  return (
    <a href={preview.url} target="_blank" rel="noopener noreferrer" className="block bg-black/30 border border-white/10 rounded overflow-hidden hover:border-nexus-cyan/40 transition" data-testid="link-preview-card">
      <div className="flex items-stretch gap-3">
        {preview.image && (
          <div className="w-28 h-28 flex-shrink-0 bg-black/50 overflow-hidden">
            <img src={preview.image} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="flex-1 min-w-0 p-3">
          <div className="text-[9px] font-mono tracking-widest uppercase text-nexus-cyan truncate">{preview.site_name}</div>
          <div className="font-display font-bold text-sm mt-0.5 line-clamp-2">{preview.title}</div>
          {preview.description && <div className="text-[11px] text-white/60 mt-1 line-clamp-2">{preview.description}</div>}
        </div>
      </div>
    </a>
  );
}

// --------------------------------------------------------------------------
function GroupPostCard({ post, group, canMod, meId, onChange }) {
  const [open, setOpen] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post.like_count || post.likes || 0);
  const isMine = meId === post.user_id;
  const pinned = (group.pinned_post_ids || []).includes(post.id);
  const act = async (path, data = null) => {
    try {
      await api.post(path, data || {});
      toast.success("Done");
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setOpen(false);
  };
  const del = async () => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/groups/posts/${post.id}`);
      toast.success("Deleted");
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setOpen(false);
  };
  const toggleLike = async () => {
    try {
      const { data } = await api.post(`/posts/${post.id}/like`);
      setLiked(!!data.active);
      setLikeCount((n) => n + (data.active ? 1 : -1));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const share = async () => {
    const url = `${window.location.origin}/group/${group.id}#post-${post.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${post.user_name} on ${group.name}`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      }
    } catch { /* user dismissed */ }
  };
  const blockAuthor = async () => {
    if (!window.confirm(`Block ${post.user_name}? Their posts and activity won't be shown to you anymore.`)) return;
    try {
      await api.post(`/users/${post.user_id}/block`);
      toast.success(`Blocked ${post.user_name}`);
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setOpen(false);
  };
  return (
    <div id={`post-${post.id}`} className={`bg-nexus-surface border rounded p-4 space-y-3 ${pinned ? "border-nexus-amber/50" : "border-white/10"}`} data-testid={`group-post-${post.id}`}>
      {pinned && (
        <div className="inline-flex items-center gap-1 text-[9px] tracking-[0.3em] uppercase text-nexus-amber"><Pin className="w-3 h-3" /> Pinned</div>
      )}
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-nexus-violet to-nexus-cyan overflow-hidden flex-shrink-0">
          {post.user_avatar && <img src={post.user_avatar} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{post.user_name}</div>
          <div className="text-[10px] text-white/40">{new Date(post.ts).toLocaleString()}</div>
        </div>
        <div className="relative">
          <button onClick={() => setOpen(!open)} className="text-white/50 hover:text-white p-1" data-testid={`post-menu-${post.id}`}>
            <MoreVertical className="w-4 h-4" />
          </button>
          {open && (
            <div className="absolute right-0 top-7 bg-nexus-panel border border-white/10 rounded shadow-lg py-1 min-w-[200px] z-10">
              {canMod && (
                <button onClick={() => act(pinned ? `/groups/posts/${post.id}/unpin` : `/groups/posts/${post.id}/pin`)} className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 flex items-center gap-2">
                  {pinned ? <><PinOff className="w-3 h-3" /> Unpin</> : <><Pin className="w-3 h-3" /> Pin to top</>}
                </button>
              )}
              {canMod && (
                <button onClick={() => act(`/groups/posts/${post.id}/lock-comments`)} className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 flex items-center gap-2">
                  {post.comments_locked ? <><Unlock className="w-3 h-3" /> Unlock comments</> : <><LockIcon className="w-3 h-3" /> Lock comments</>}
                </button>
              )}
              {!isMine && meId && (
                <button onClick={blockAuthor} className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 flex items-center gap-2 text-nexus-amber" data-testid={`post-block-${post.id}`}>
                  <Ban className="w-3 h-3" /> Block {post.user_name}
                </button>
              )}
              {(isMine || canMod) && (
                <button onClick={del} className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 flex items-center gap-2 text-nexus-crimson" data-testid={`post-delete-${post.id}`}>
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {post.content && <div className="text-sm whitespace-pre-wrap">{post.content}</div>}
      {post.media_urls && post.media_urls.length > 0 && (
        <div className={`grid gap-1 ${post.media_urls.length === 1 ? "" : post.media_urls.length === 2 ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3"}`}>
          {post.media_urls.map((u, i) => (
            post.kind === "video" || post.kind === "reel"
              ? <video key={i} src={u} controls className="w-full rounded" />
              : <img key={i} src={u} alt="" className="w-full rounded object-cover aspect-square" />
          ))}
        </div>
      )}
      {post.link_preview && <LinkPreviewCard preview={post.link_preview} />}
      {post.link_url && !post.link_preview && (
        <a href={post.link_url} target="_blank" rel="noopener noreferrer" className="text-nexus-cyan text-xs underline break-all">{post.link_url}</a>
      )}
      {/* Action row — like / share, visible on every post */}
      <div className="flex items-center gap-3 pt-2 border-t border-white/5">
        <button onClick={toggleLike} className={`inline-flex items-center gap-1.5 text-xs ${liked ? "text-nexus-crimson" : "text-white/60"} hover:text-nexus-crimson transition`} data-testid={`post-like-${post.id}`}>
          <Heart className={`w-4 h-4 ${liked ? "fill-current" : ""}`} /> {likeCount || "Like"}
        </button>
        <button onClick={share} className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-nexus-cyan transition" data-testid={`post-share-${post.id}`}>
          <Share2 className="w-4 h-4" /> Share
        </button>
        {post.comments_locked && (
          <div className="text-[10px] text-white/40 flex items-center gap-1 ml-auto"><LockIcon className="w-3 h-3" /> Comments locked</div>
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
function MembersList({ members, group, canMod, meId, onChange }) {
  const [busyId, setBusyId] = useState(null);
  const act = async (uid, path, data = null) => {
    setBusyId(uid);
    try {
      await api.post(path, data || {});
      toast.success("Done");
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusyId(null);
  };
  const blockMember = async (uid, name) => {
    if (!window.confirm(`Block ${name}? Their activity won't be shown to you anywhere on NEXUS and they can't follow or friend-request you until you unblock.`)) return;
    try {
      await api.post(`/users/${uid}/block`);
      toast.success(`Blocked ${name}`);
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const isOwner = group.my_role === "owner";
  return (
    <div className="space-y-2" data-testid="members-list">
      {members.map((m) => (
        <div key={m.id} className="bg-nexus-surface border border-white/10 rounded p-3 flex items-center gap-3" data-testid={`member-${m.user_id}`}>
          <Link to={`/u/${m.user_id}`} className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-nexus-violet to-nexus-cyan overflow-hidden flex-shrink-0">
              {m.avatar && <img src={m.avatar} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{m.display_name}</div>
              <div className="text-[10px] text-white/50">{m.role}</div>
            </div>
          </Link>
          {m.role === "owner" && <Shield className="w-4 h-4 text-nexus-amber" title="Owner" />}
          {m.role === "admin" && <Shield className="w-4 h-4 text-nexus-cyan" title="Admin" />}
          {canMod && m.user_id !== meId && m.role !== "owner" && (
            <div className="flex items-center gap-1">
              {isOwner && (
                <button
                  onClick={() => act(m.user_id, `/groups/${group.id}/members/${m.user_id}/role`, { role: m.role === "admin" ? "member" : "admin" })}
                  disabled={busyId === m.user_id}
                  className="text-[9px] uppercase tracking-wider px-2 py-1 bg-white/5 hover:bg-white/10 rounded"
                  data-testid={`member-role-${m.user_id}`}
                >
                  {m.role === "admin" ? "Demote" : "Make admin"}
                </button>
              )}
              <button
                onClick={() => act(m.user_id, `/groups/${group.id}/members/${m.user_id}/ban`)}
                disabled={busyId === m.user_id}
                className="text-[9px] uppercase tracking-wider px-2 py-1 bg-nexus-crimson/20 text-nexus-crimson hover:bg-nexus-crimson/30 rounded"
                data-testid={`member-ban-${m.user_id}`}
              >
                Ban
              </button>
            </div>
          )}
          {m.user_id !== meId && meId && (
            <button
              onClick={() => blockMember(m.user_id, m.display_name)}
              className="text-[9px] uppercase tracking-wider px-2 py-1 bg-nexus-amber/15 text-nexus-amber hover:bg-nexus-amber/25 rounded inline-flex items-center gap-1"
              data-testid={`member-block-${m.user_id}`}
              title="Block this member — their posts and activity will be hidden from you"
            >
              <Ban className="w-3 h-3" /> Block
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// --------------------------------------------------------------------------
function ModerationPanel({ groupId, pendingPosts, pendingMembers, onChange }) {
  const actPost = async (pid, action) => {
    try {
      await api.post(`/groups/posts/${pid}/${action}`);
      toast.success(action === "approve" ? "Approved" : "Rejected");
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const actMember = async (uid, action) => {
    try {
      await api.post(`/groups/${groupId}/members/${uid}/${action}`);
      toast.success(action === "approve" ? "Approved" : "Rejected");
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-6" data-testid="moderation-panel">
      <section>
        <div className="label-eyebrow mb-2">// pending posts · {pendingPosts.length}</div>
        {pendingPosts.length === 0 && <div className="text-white/50 text-sm">Nothing waiting on approval.</div>}
        <div className="space-y-2">
          {pendingPosts.map((p) => (
            <div key={p.id} className="bg-nexus-surface border border-white/10 rounded p-3" data-testid={`pending-post-${p.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="text-xs font-medium">{p.user_name}</div>
                <div className="text-[10px] text-white/40">{new Date(p.ts).toLocaleString()}</div>
                <div className="ml-auto flex gap-2">
                  <button onClick={() => actPost(p.id, "approve")} className="btn-nexus !py-1 !px-3 text-[9px]" data-testid={`approve-post-${p.id}`}><CheckCircle2 className="w-3 h-3" /> Approve</button>
                  <button onClick={() => actPost(p.id, "reject")} className="btn-ghost !py-1 !px-3 text-[9px] !text-nexus-crimson" data-testid={`reject-post-${p.id}`}><XCircle className="w-3 h-3" /> Reject</button>
                </div>
              </div>
              <div className="text-sm whitespace-pre-wrap">{p.content}</div>
              {p.media_url && <img src={p.media_url} alt="" className="w-full max-h-64 object-cover rounded mt-2" />}
            </div>
          ))}
        </div>
      </section>
      <section>
        <div className="label-eyebrow mb-2">// pending join requests · {pendingMembers.length}</div>
        {pendingMembers.length === 0 && <div className="text-white/50 text-sm">Nobody is waiting to join.</div>}
        <div className="space-y-2">
          {pendingMembers.map((m) => (
            <div key={m.id} className="bg-nexus-surface border border-white/10 rounded p-3 flex items-center gap-3" data-testid={`pending-member-${m.user_id}`}>
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-nexus-violet to-nexus-cyan overflow-hidden">
                {m.avatar && <img src={m.avatar} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 text-sm">{m.display_name}</div>
              <button onClick={() => actMember(m.user_id, "approve")} className="btn-nexus !py-1 !px-3 text-[9px]"><CheckCircle2 className="w-3 h-3" /> Approve</button>
              <button onClick={() => actMember(m.user_id, "reject")} className="btn-ghost !py-1 !px-3 text-[9px] !text-nexus-crimson"><XCircle className="w-3 h-3" /> Reject</button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// --------------------------------------------------------------------------
function GroupSettingsPanel({ group, onSaved }) {
  const [form, setForm] = useState({
    name: group.name,
    group_type: group.group_type,
    description: group.description || "",
    privacy: group.privacy,
    age_min: group.age_min,
    category: group.category,
    cover_url: group.cover_url || "",
    cover_pos_y: group.cover_pos_y ?? 50,
    avatar_url: group.avatar_url || "",
    rules: [...(group.rules || []), ""],
    allowed_media: [...(group.allowed_media || ["image", "video", "reel", "link", "text"])],
    require_post_approval: !!group.require_post_approval,
    allow_invites_by_members: group.allow_invites_by_members !== false,
  });
  const [busy, setBusy] = useState(false);

  const toggleMedia = (k) => {
    setForm((f) => ({
      ...f,
      allowed_media: f.allowed_media.includes(k) ? f.allowed_media.filter((x) => x !== k) : [...f.allowed_media, k],
    }));
  };
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, rules: form.rules.map((r) => r.trim()).filter(Boolean) };
      await api.patch(`/groups/${group.id}`, body);
      toast.success("Settings saved");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };
  const danger = async () => {
    if (!window.confirm(`Permanently delete "${group.name}"? This also removes every post and membership.`)) return;
    try {
      await api.delete(`/groups/${group.id}`);
      toast.success("Group deleted");
      window.location.href = "/groups";
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="group-settings">
      <div className="label-eyebrow">// group settings</div>
      <GroupImageEditor
        label="Group banner"
        testId="banner-editor"
        aspect="banner"
        url={form.cover_url}
        pos={form.cover_pos_y}
        onUrl={(u) => setForm({ ...form, cover_url: u })}
        onPos={(p) => setForm({ ...form, cover_pos_y: p })}
      />
      <GroupImageEditor
        label="Group profile image"
        testId="avatar-editor"
        aspect="avatar"
        url={form.avatar_url}
        onUrl={(u) => setForm({ ...form, avatar_url: u })}
      />
      <div className="bg-nexus-surface border border-white/10 rounded p-5 space-y-4">
        <Field label="Name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" data-testid="settings-name" /></Field>
        <Field label="Group type · title"><input value={form.group_type} onChange={(e) => setForm({ ...form, group_type: e.target.value })} className="input" data-testid="settings-type" /></Field>
        <Field label="Description"><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input" /></Field>
        <Field label="Cover image URL"><input value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} className="input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Privacy">
            <div className="flex gap-2">
              {["public", "private"].map((p) => (
                <button key={p} onClick={() => setForm({ ...form, privacy: p })} className={`flex-1 px-3 py-2 text-[11px] uppercase tracking-widest border rounded ${form.privacy === p ? "bg-nexus-cyan text-black border-nexus-cyan" : "border-white/10 text-white/60"}`} data-testid={`settings-privacy-${p}`}>
                  {p === "public" ? <><Globe className="w-3 h-3 inline mr-1" /> Public</> : <><Lock className="w-3 h-3 inline mr-1" /> Private</>}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Minimum age">
            <input type="number" min={0} max={99} value={form.age_min} onChange={(e) => setForm({ ...form, age_min: parseInt(e.target.value) || 0 })} className="input" data-testid="settings-age" />
          </Field>
        </div>
        <Field label="Allowed content types">
          <div className="flex flex-wrap gap-2">
            {KINDS.map((k) => {
              const on = form.allowed_media.includes(k.id);
              return (
                <button key={k.id} onClick={() => toggleMedia(k.id)} className={`inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-widest border rounded ${on ? "bg-nexus-cyan text-black border-nexus-cyan" : "border-white/10 text-white/50"}`} data-testid={`settings-media-${k.id}`}>
                  <k.icon className="w-3 h-3" /> {k.label}
                </button>
              );
            })}
          </div>
        </Field>
        <ToggleRow
          label="Require admin approval for member posts"
          sub="When ON, non-admin posts land in the moderation queue first."
          value={form.require_post_approval}
          onChange={(v) => setForm({ ...form, require_post_approval: v })}
          testId="settings-require-approval"
        />
        <ToggleRow
          label="Any member can invite friends"
          sub="Turn OFF to restrict invites to owner + admins."
          value={form.allow_invites_by_members}
          onChange={(v) => setForm({ ...form, allow_invites_by_members: v })}
          testId="settings-member-invites"
        />
        <Field label="Group rules">
          {form.rules.map((r, i) => (
            <input
              key={i}
              value={r}
              onChange={(e) => {
                const next = [...form.rules]; next[i] = e.target.value;
                if (i === next.length - 1 && e.target.value.trim()) next.push("");
                setForm({ ...form, rules: next });
              }}
              placeholder={`Rule ${i + 1}`}
              className="input mb-1"
            />
          ))}
        </Field>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
          <button onClick={save} disabled={busy} className="btn-nexus !py-2 !px-5 text-[10px]" data-testid="settings-save">
            {busy ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>
      {group.my_role === "owner" && (
        <div className="bg-nexus-crimson/10 border border-nexus-crimson/30 rounded p-5 space-y-2">
          <div className="label-eyebrow text-nexus-crimson">// danger zone</div>
          <p className="text-xs text-white/70">Delete this group forever. All posts, membership records and invites are removed. This cannot be undone.</p>
          <button onClick={danger} className="btn-ghost !text-nexus-crimson !border-nexus-crimson/40 text-[10px]" data-testid="settings-delete">
            <Trash2 className="w-3 h-3" /> Delete group permanently
          </button>
        </div>
      )}
      <style>{`.input { width: 100%; background: transparent; border: 1px solid rgba(255,255,255,0.1); padding: 8px 12px; font-size: 13px; }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1">{label}</div>
      {children}
    </label>
  );
}

function ToggleRow({ label, sub, value, onChange, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-t border-white/5">
      <div className="min-w-0">
        <div className="text-sm font-medium">{label}</div>
        {sub && <div className="text-[10px] text-white/40 mt-0.5">{sub}</div>}
      </div>
      <button
        onClick={() => onChange(!value)}
        className={`relative w-11 h-6 rounded-full transition flex-shrink-0 ${value ? "bg-nexus-cyan" : "bg-white/15"}`}
        data-testid={testId}
      >
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${value ? "left-5" : "left-0.5"}`} />
      </button>
    </div>
  );
}


// --------------------------------------------------------------------------
// GroupImageEditor — upload + reposition for banner (object-position-y) and
// a square avatar. Uses /api/upload/image. For banner, a vertical slider lets
// the owner reframe the image without re-uploading.
// --------------------------------------------------------------------------
function GroupImageEditor({ label, testId, aspect, url, pos = 50, onUrl, onPos }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const isBanner = aspect === "banner";
  const pick = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Pick an image"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUrl?.(data.url);
      toast.success("Uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };
  return (
    <div className="bg-nexus-surface border border-white/10 rounded p-4 space-y-3" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">// {label}</div>
        <div className="flex items-center gap-2">
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid={`${testId}-input`} />
          <button onClick={() => inputRef.current?.click()} disabled={uploading} className="btn-ghost text-[10px]" data-testid={`${testId}-upload`}>
            <Upload className="w-3 h-3" /> {uploading ? "Uploading…" : url ? "Replace" : "Upload"}
          </button>
          {url && (
            <button onClick={() => onUrl?.("")} className="btn-ghost text-[10px] !text-nexus-crimson" data-testid={`${testId}-clear`}>
              <X className="w-3 h-3" /> Remove
            </button>
          )}
        </div>
      </div>
      {isBanner ? (
        <div className="aspect-[5/1] bg-black/40 overflow-hidden rounded">
          {url ? (
            <img src={url} alt="" className="w-full h-full object-cover" style={{ objectPosition: `center ${pos}%` }} />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">No banner set</div>
          )}
        </div>
      ) : (
        <div className="w-24 h-24 rounded-full bg-black/40 overflow-hidden ring-1 ring-white/10">
          {url ? (
            <img src={url} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/30"><Users className="w-5 h-5" /></div>
          )}
        </div>
      )}
      {isBanner && url && (
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1">Reposition · vertical ({pos}%)</div>
          <input
            type="range"
            min={0}
            max={100}
            value={pos}
            onChange={(e) => onPos?.(parseInt(e.target.value))}
            className="w-full accent-nexus-cyan"
            data-testid={`${testId}-reposition`}
          />
          <div className="flex justify-between text-[9px] text-white/40 mt-1 font-mono">
            <span>top</span><span>center</span><span>bottom</span>
          </div>
        </div>
      )}
    </div>
  );
}
